import { instance } from "api/apiBase";
import { NavLinkConfig } from "./constants";
import { NavSection } from "./use-permitted-nav-sections";

export interface NavSettings {
  order: string[];
  hidden: string[];
}

export interface ResolvedNavItem extends NavLinkConfig {
  isHidden: boolean;
}

export interface ResolvedNavSection extends Omit<NavSection, "items"> {
  items: ResolvedNavItem[];
}

export const DEFAULT_NAV_SETTINGS: NavSettings = { order: [], hidden: [] };

export const updateNavSettings = (navSettings: NavSettings) =>
  instance.patch("/portal/v1/users/ui_settings", {
    ui_settings: { nav: navSettings }
  });

export const resolveNavSections = (
  sections: NavSection[],
  settings: NavSettings
): ResolvedNavSection[] => {
  const rank = new Map(settings.order.map((id, index) => [id, index]));
  const hidden = new Set(settings.hidden);
  const unranked = settings.order.length;

  return sections.map((section) => ({
    ...section,
    items: section.items
      .map((item, defaultIndex) => ({
        item,
        sortKey: rank.get(item.id) ?? unranked + defaultIndex
      }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(({ item }) => ({ ...item, isHidden: hidden.has(item.id) }))
  }));
};

export const serializeNavSections = (
  sections: ResolvedNavSection[]
): NavSettings => {
  const items = sections.flatMap((section) => section.items);

  return {
    order: items.map(({ id }) => id),
    hidden: items.filter(({ isHidden }) => isHidden).map(({ id }) => id)
  };
};

export const isDefaultNavLayout = (
  defaults: NavSection[],
  sections: ResolvedNavSection[]
) =>
  sections.every((section, sectionIndex) =>
    section.items.every(
      (item, index) =>
        !item.isHidden && item.id === defaults[sectionIndex]?.items[index]?.id
    )
  );

export const navSettingsEqual = (a: NavSettings, b: NavSettings) =>
  a.order.join() === b.order.join() && a.hidden.join() === b.hidden.join();
